// Opening the app's pages from MizuIQ's chat (python agentic_ai/navigation.py).
// An answer that opens a page comes with its navigation - { page, label,
// params } by the Modules menu's page keys (mw2/context.js) and the New
// Dashboard's tabs, or { back: true } - and the window opens it
// (navigationTarget). One that asks which page comes with { choices }.
//
// The message box's commands: /open <page>, /pages, /back, /help - typed
// with "/", the window suggests them, and then the pages (commandSuggestions).
import dayjs from "dayjs";
import { PAGES, menuGroups, withContext } from "../mw2/context";
import { TAB_GROUPS } from "../../pages/NewDashboard/DashboardHeader";

// The New Dashboard's tabs, by navigation.py's keys.
const DASHBOARD_TABS = {
  "mizu-ai": "alerts",
  "digital-twin": "twin",
  hydraulics: "hydraulics",
  plant: "plant",
  efficiency: "efficiency",
  workforce: "workforce",
  financial: "financial",
};

// The context a page may be opened with, each value as its URL reads it -
// anything else is left out.
const PRESETS = ["D", "W", "1M", "3M", "6M", "YTD", "1Y"];
const CHECKS = {
  meter: (v) => /^[A-Za-z0-9][A-Za-z0-9_.-]{0,39}$/.test(v),
  range: (v) => PRESETS.includes(v),
  from: (v) => /^\d{4}-\d{2}-\d{2}$/.test(v),
  to: (v) => /^\d{4}-\d{2}-\d{2}$/.test(v),
  system: (v) => ["total", "plant", "potable", "non_potable"].includes(v),
  view: (v) => ["accountability", "intake", "supply", "demand"].includes(v),
  district: (v) => /^[A-Za-z0-9_]{2,20}$/.test(v),
  // A meter category's name, as the leak and anomaly APIs take it
  // ("Non-Potable Irrigation - West") - "ALL" is no filter.
  category: (v) => /^[\w .,&()/'-]{1,80}$/.test(v) && !/^all$/i.test(v),
};

function cleanParams(params) {
  const clean = {};
  if (!params || typeof params !== "object") return clean;
  Object.entries(CHECKS).forEach(([key, ok]) => {
    const value = params[key] === null || params[key] === undefined ? "" : String(params[key]).trim();
    if (value && ok(value)) clean[key] = value;
  });
  return clean;
}

// A key's own entry - never one of Object's ("__proto__").
const own = (table, key) => (typeof key === "string" && Object.prototype.hasOwnProperty.call(table, key) ? table[key] : null);

// Where the page was opened from, for its source chip and Back (context.js
// sourceOf): the Modules menu page the user was on - or just the chat.
const sourceFrom = (here) => (own(PAGES, here?.key) ? `chat.${here.key}` : "chat");

/**
 * Where a navigation goes, from `here` (context.js pageAt): { to, state } - a
 * page's URL, with the context it was asked with - or { dashboard, district }
 * for the New Dashboard's tabs and its map (a requestDashboard request, as
 * the answers' buttons make), or { back: true }. null when it is none this
 * window can open.
 */
export function navigationTarget(navigation, here) {
  if (!navigation || typeof navigation !== "object") return null;
  if (navigation.back === true) return { back: true };
  const key = navigation.page;
  const params = cleanParams(navigation.params);
  const src = sourceFrom(here);
  const tab = own(DASHBOARD_TABS, key);
  if (tab) return { dashboard: { kind: "tab", tab }, district: params.district };
  if (key === "meter-map") {
    return params.meter
      ? { dashboard: { kind: "locate", meterIds: [params.meter], label: "meter from MizuIQ" }, district: params.district }
      : null;
  }
  // A meter's page, as the app's own links open it.
  if (key === "meter-detail") {
    if (!params.meter) return null;
    const since = params.from || dayjs().subtract(30, "day").format("YYYY-MM-DD");
    return {
      to: withContext("/meter-leak-details", { category: "ALL", meter_id: params.meter, time_range: "Current", selected_time: since, district: params.district, src }),
    };
  }
  const page = own(PAGES, key);
  if (!page) return null;
  return { to: withContext(page.to, { ...params, src }), state: page.state };
}

// Flow Analytics' systems, as its picker names them (TotalFlow.jsx).
const SYSTEM_WORDS = { total: "total system", plant: "potable system", potable: "potable", non_potable: "non-potable system" };

/** A navigation's context in words, for its card: "meter 126171 · past week". */
export function navigationDetails(navigation) {
  const params = cleanParams(navigation?.params);
  const words = { D: "last full day", W: "past week", "1M": "past month", "3M": "past 3 months", "6M": "past 6 months", YTD: "year to date", "1Y": "past year" };
  const day = (value) => dayjs(value).format("MMM D, YYYY");
  return [
    params.meter ? `meter ${params.meter}` : null,
    params.range ? words[params.range] : params.from ? (params.to && params.to !== params.from ? `${day(params.from)} – ${day(params.to)}` : day(params.from)) : null,
    params.system ? SYSTEM_WORDS[params.system] : null,
    params.category || null,
    params.view ? `${params.view} view` : null,
    params.district ? `district ${params.district}` : null,
  ].filter(Boolean);
}

// ── Commands ─────────────────────────────────────────────────────────────────

export const COMMANDS = [
  { name: "open", usage: "/open <page>", hint: "Open a page", insert: "/open " },
  { name: "pages", usage: "/pages", hint: "The pages I can open", send: "/pages" },
  { name: "back", usage: "/back", hint: "Back to the page before", send: "/back" },
  { name: "help", usage: "/help", hint: "What you can ask", send: "/help" },
];
const OPENERS = ["open", "go", "goto", "navigate", "page"];
const MAX_SUGGESTIONS = 8;

// The pages a command may name, as the menu lists them for this user, then
// the dashboard's tabs.
function commandPages() {
  const pages = menuGroups().flatMap((group) => group.pages.map((page) => ({ label: page.label, group: group.label })));
  const tabs = TAB_GROUPS.flatMap((group) => group.tabs.map((tab) => ({ label: tab.label, group: "Dashboard" })));
  return [...tabs.slice(0, 2), ...pages, ...tabs.slice(2)];
}

/**
 * What the message box suggests for what is typed: after "/", the commands
 * whose names start with it; after "/open " (or /go), the pages whose names
 * have words starting with what follows - each { key, label, hint, insert }
 * (put in the box) or { key, label, hint, send } (asked at once).
 */
export function commandSuggestions(text) {
  const value = String(text || "");
  const match = /^\/(\w*)(\s+(.*))?$/s.exec(value);
  if (!match) return [];
  const name = match[1].toLowerCase();
  if (match[2] === undefined) {
    return COMMANDS.filter((command) => command.name.startsWith(name)).map((command) => ({
      key: `command-${command.name}`,
      label: command.usage,
      hint: command.hint,
      insert: command.insert,
      send: command.send,
    }));
  }
  if (!OPENERS.includes(name)) return [];
  const words = (match[3] || "").toLowerCase().replace(/&/g, " and ").split(/\s+/).filter(Boolean);
  return commandPages()
    .filter((page) => {
      const parts = page.label.toLowerCase().replace(/&/g, " and ").split(/\s+/);
      return words.every((word) => parts.some((part) => part.startsWith(word)));
    })
    .slice(0, MAX_SUGGESTIONS)
    .map((page) => ({ key: `page-${page.label}`, label: page.label, hint: page.group, send: `/open ${page.label}` }));
}
